import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiUser, FiMail, FiPhone, FiLock, FiAlertCircle, FiCheckCircle, FiEye, FiEyeOff, FiMessageSquare, FiZap, FiShield, FiTruck } from 'react-icons/fi';
import { GoogleLogin } from '@react-oauth/google';
import { motion } from 'framer-motion';

export default function Register() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    // ✅ PHONE OTP (primary method)
    const [otpName, setOtpName] = useState('');
    const [otpPhone, setOtpPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const navigate = useNavigate();
    const { register, loginWithGoogle, sendPhoneOtp, verifyPhoneOtp } = useAuth();

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        if (error) setError('');
    };

    // ✅ EMAIL/PASSWORD REGISTRATION
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword) {
            setError('All fields are required');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const result = await register(formData);

        if (result.success) {
            setSuccess(result.message || 'Account created! Redirecting to login...');
            setTimeout(() => navigate('/login'), 2000);
        } else {
            setError(result.message || 'Registration failed.');
        }

        setLoading(false);
    };

    // ✅ GOOGLE REGISTRATION - Uses AuthContext
    const handleGoogleSignUp = async (response) => {
        setGoogleLoading(true);
        setError('');
        setSuccess('');

        const result = await loginWithGoogle(response.credential);

        if (result.success) {
            setSuccess('Google Sign-Up successful! Redirecting...');
            setTimeout(() => navigate('/account'), 1000);
        } else {
            setError(result.message);
        }

        setGoogleLoading(false);
    };

    const handleGoogleError = () => {
        setError('Google sign-up was cancelled or could not be started. Please try again.');
    };

    // ✅ PHONE: SEND OTP
    const handleSendOtp = async (e) => {
        e?.preventDefault?.();
        const digits = otpPhone.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '').replace(/^0(?=\d{10}$)/, '');
        if (!/^[6-9]\d{9}$/.test(digits)) {
            setError('Enter a valid 10-digit mobile number');
            return;
        }
        setOtpLoading(true);
        setError('');
        setSuccess('');
        const result = await sendPhoneOtp(digits);
        if (result.success) {
            setOtpSent(true);
            setSuccess(result.message || 'OTP sent to your mobile number');
            setCooldown(30);
        } else {
            setError(result.message);
        }
        setOtpLoading(false);
    };

    // ✅ PHONE: VERIFY OTP (passes name so new accounts get a proper fullName)
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!/^\d{4,6}$/.test(otp.trim())) {
            setError('Enter the 4-digit OTP sent to your phone');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        const result = await verifyPhoneOtp(otpPhone.replace(/\D/g, ''), otp.trim(), otpName.trim() || undefined);
        if (result.success) {
            setSuccess('Account verified! Redirecting...');
            setTimeout(() => navigate('/account'), 1000);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 px-4 py-8">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-5xl w-full mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-6 items-stretch"
            >
                {/* Left: brand panel (desktop) */}
                <div className="hidden lg:flex flex-col justify-center rounded-2xl bg-gradient-to-br from-red-600 to-red-800 text-white p-10 shadow-lg">
                    <h1 className="text-4xl font-bold mb-3">Create Your Account</h1>
                    <p className="text-red-100 mb-8">Join thousands of happy customers on Havbit. Sign up in seconds — no password needed.</p>
                    <ul className="space-y-5 text-sm">
                        <li className="flex items-start gap-3">
                            <span className="mt-0.5 rounded-full bg-white/20 p-2"><FiZap className="h-4 w-4" /></span>
                            <div><p className="font-semibold">Fastest: phone + OTP</p><p className="text-red-100">Enter your mobile number, verify the 4-digit SMS code, done.</p></div>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="mt-0.5 rounded-full bg-white/20 p-2"><FiShield className="h-4 w-4" /></span>
                            <div><p className="font-semibold">Secure by default</p><p className="text-red-100">OTPs expire in 5 minutes and are rate-limited.</p></div>
                        </li>
                        <li className="flex items-start gap-3">
                            <span className="mt-0.5 rounded-full bg-white/20 p-2"><FiTruck className="h-4 w-4" /></span>
                            <div><p className="font-semibold">Track orders & faster checkout</p><p className="text-red-100">One account for orders, wishlist and support.</p></div>
                        </li>
                    </ul>
                </div>

                {/* Right: form card */}
                <div>
                    <div className="text-center mb-6 lg:hidden">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Account</h1>
                        <p className="text-gray-600">Join thousands of happy customers on Havbit</p>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-8">
                        {success && (
                            <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center">
                                <FiCheckCircle className="mr-2 shrink-0" />
                                <span>{success}</span>
                            </div>
                        )}

                        {error && (
                            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
                                <FiAlertCircle className="mr-2 mt-0.5 shrink-0" />
                                <div>
                                    <span className="font-medium">Error:</span>
                                    <span className="ml-2">{error}</span>
                                </div>
                            </div>
                        )}

                        {/* 1️⃣ PRIMARY: PHONE + OTP */}
                        <div className="rounded-xl border-2 border-red-500 bg-red-50/50 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <FiPhone className="text-red-600" /> Sign up with phone
                                </h2>
                                <span className="text-[11px] font-semibold uppercase tracking-wide bg-red-600 text-white px-2.5 py-1 rounded-full">Recommended</span>
                            </div>

                            {!otpSent ? (
                                <form onSubmit={handleSendOtp} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                                        <div className="relative">
                                            <FiUser className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                            <input
                                                type="text"
                                                value={otpName}
                                                onChange={(e) => setOtpName(e.target.value)}
                                                className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white"
                                                placeholder="John Doe"
                                                disabled={otpLoading || loading}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number *</label>
                                        <div className="relative">
                                            <FiPhone className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                            <input
                                                type="tel"
                                                required
                                                value={otpPhone}
                                                onChange={(e) => setOtpPhone(e.target.value.replace(/\D/g, '').slice(0, 13))}
                                                className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none bg-white"
                                                placeholder="9876543210"
                                                disabled={otpLoading || loading}
                                                maxLength="13"
                                            />
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500">A 4-digit OTP will be sent via SMS. No password needed.</p>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={otpLoading}
                                        className="w-full py-3.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-all duration-300 disabled:opacity-50 flex items-center justify-center"
                                    >
                                        {otpLoading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Sending OTP...
                                            </>
                                        ) : 'Send OTP'}
                                    </button>
                                </form>
                            ) : (
                                <form onSubmit={handleVerifyOtp} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            OTP sent to +91 {otpPhone.replace(/\D/g, '').slice(-10)}
                                        </label>
                                        <div className="relative">
                                            <FiMessageSquare className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                required
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none tracking-[0.5em] text-center font-semibold bg-white"
                                                placeholder="••••"
                                                disabled={loading}
                                                maxLength="6"
                                            />
                                        </div>
                                        <p className="mt-2 text-xs text-gray-500">OTP is valid for 5 minutes.</p>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-3.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-medium hover:from-red-700 hover:to-red-800 transition-all duration-300 disabled:opacity-50 flex items-center justify-center"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Verifying...
                                            </>
                                        ) : 'Verify & Create Account'}
                                    </button>
                                    <div className="flex items-center justify-between text-sm">
                                        <button
                                            type="button"
                                            onClick={() => { setOtpSent(false); setOtp(''); setError(''); setSuccess(''); }}
                                            className="text-gray-600 hover:text-gray-800 hover:underline"
                                        >
                                            Change number
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSendOtp}
                                            disabled={otpLoading || cooldown > 0}
                                            className="text-red-600 font-medium hover:text-red-700 hover:underline disabled:opacity-50 disabled:no-underline"
                                        >
                                            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* 2️⃣ GOOGLE */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-white text-gray-500">or continue with</span>
                            </div>
                        </div>
                        <div className="mb-2">
                            {googleLoading ? (
                                <div className="w-full flex items-center justify-center gap-3 py-3.5 text-gray-600">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                                    Creating account...
                                </div>
                            ) : (
                                <div className="flex justify-center">
                                    <GoogleLogin
                                        onSuccess={handleGoogleSignUp}
                                        onError={handleGoogleError}
                                        text="signup_with"
                                        shape="rectangular"
                                        size="large"
                                        width="280"
                                    />
                                </div>
                            )}
                        </div>

                        {/* 3️⃣ EMAIL SIGNUP (all fields preserved) */}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-4 bg-white text-gray-500">Or sign up with email</span>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Full Name *
                                    </label>
                                    <div className="relative">
                                        <FiUser className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            placeholder="John Doe"
                                            disabled={loading || googleLoading}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number *
                                    </label>
                                    <div className="relative">
                                        <FiPhone className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                        <input
                                            type="tel"
                                            name="phone"
                                            required
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            placeholder="9876543210"
                                            disabled={loading || googleLoading}
                                            pattern="[0-9]{10}"
                                            maxLength="10"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address *
                                </label>
                                <div className="relative">
                                    <FiMail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                        placeholder="you@example.com"
                                        disabled={loading || googleLoading}
                                    />
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Password *
                                    </label>
                                    <div className="relative">
                                        <FiLock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            required
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            placeholder="At least 6 characters"
                                            disabled={loading || googleLoading}
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 top-3.5"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FiEyeOff className="h-5 w-5 text-gray-400" /> : <FiEye className="h-5 w-5 text-gray-400" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm Password *
                                    </label>
                                    <div className="relative">
                                        <FiLock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            required
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
                                            placeholder="Re-enter your password"
                                            disabled={loading || googleLoading}
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 top-3.5"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? <FiEyeOff className="h-5 w-5 text-gray-400" /> : <FiEye className="h-5 w-5 text-gray-400" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={loading || googleLoading}
                                    className="w-full py-3.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 flex items-center justify-center"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Creating Account...
                                        </>
                                    ) : 'Create Account with Email'}
                                </button>
                            </div>
                        </form>

                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <p className="text-center text-gray-600">
                                Already have an account?{' '}
                                <Link to="/login" className="text-red-600 font-medium hover:text-red-700 hover:underline">
                                    Sign in here
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
